import { Box } from "@mui/material";
import dayjs from "dayjs";
import { debounce } from "lodash";
import { memo, useCallback, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import useDeepCompareEffect from "use-deep-compare-effect";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
interface AutoSaveProps {
  defaultValues: any;
  onSubmit: (data: any) => Promise<void>;
  showSaveStatus?: boolean;
  debounceTime?: number;
}

const AutoSave = memo(
  ({
    defaultValues,
    onSubmit,
    showSaveStatus = false,
    debounceTime = 1000,
  }: AutoSaveProps) => {
    const methods = useFormContext();
    const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);

    const debouncedSave = useCallback(
      debounce(() => {
        methods
          .handleSubmit((props) =>
            onSubmit(props).then(() => {
              setLastSavedTime(dayjs().toDate());
            })
          )()

          .catch((e) => {
            throw e;
          });
      }, debounceTime),
      []
    );

    const watchedData = useWatch({
      control: methods.control,
      defaultValue: defaultValues,
    });

    useDeepCompareEffect(() => {
      if (methods.formState.isDirty) {
        debouncedSave();
      }
    }, [watchedData]);

    return (
      <Box className="flex">
        {lastSavedTime ? (
          <Box>
            <small>Last saved {dayjs(lastSavedTime).fromNow()}</small>
          </Box>
        ) : (
          <Box>
            <small>No changes saved.</small>
          </Box>
        )}
        {methods.formState.isSubmitting && showSaveStatus ? "Saving..." : null}
      </Box>
    );
  }
);

AutoSave.displayName = "AutoSave";

export default AutoSave;
